import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, MenuItem } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { CustomButton, TextField, CustomSelect } from '../../../v2/MUI_custom_component';
import { getLocationAndPincodeAction } from '../API/Action';
import { GetAllStates, GetAllCityOfState } from '../../../screen/auth/action/util_actions';
import GlassModal from '../../../v2/MUI_custom_component/Modal/GlassModal';
const AddressDetailModal = ({ 
    open, 
    onClose,
    loading = false,
    firmDetails,
    initialValidationErrors = {}
}) => {
    const dispatch = useDispatch();
    const { operational_details, gst_details, pan_details, gst_flow , existingFirm} = firmDetails;

    const [addressForm, setAddressForm] = useState({
        address_line_1: operational_details?.address_line_1,
        address_line_2: operational_details?.address_line_2,
        state_id: operational_details?.state_id,
        state_name: operational_details?.state_name,
        district_id: operational_details?.district_id,
        district_name: operational_details?.district_name,
        location_id: operational_details?.location_id,
        location_name: operational_details?.location_name,
        pincode_id: operational_details?.pincode_id,
        firm_name: operational_details?.firm_name || ''
    });
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [allCities, setAllCities] = useState([]);
    const [allPincodes, setAllPincodes] = useState([]);
    const [addressValidationErrors, setAddressValidationErrors] = useState({});
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [firstSelection, setFirstSelection] = useState(null);
    const addressFormRef = useRef(addressForm);

    useEffect(() => {
        addressFormRef.current = addressForm;
    }, [addressForm]);

    const getlocationPincode = async (districtId, locationId = null, PincodeId = null) => {
        if (!districtId) return;
        if (PincodeId && addressFormRef.current.location_id) return;
        if (locationId && addressFormRef.current.pincode_id) return;

        setIsLoadingCities(true);
        const { data, error } = await getLocationAndPincodeAction(districtId, {
            location_id: locationId,
            pincode_id: PincodeId,
        });
        setIsLoadingCities(false);
        
        if (!error) {
            if (locationId && !addressFormRef.current.pincode_id) {
                setAllPincodes(
                    data.pincode_list.map((pincode) => ({
                        label: pincode.pincode_name,
                        value: pincode.pincode_id,
                    }))
                );
            } else if (PincodeId && !addressFormRef.current.location_id) {
                setAllCities(
                    data.location_list.map((city) => ({
                        label: city.location_name,
                        value: city.location_id,
                    }))
                );
            } else if (!locationId && !PincodeId) {
                setAllPincodes(
                    data.pincode_list.map((pincode) => ({
                        label: pincode.pincode_name,
                        value: pincode.pincode_id,
                    }))
                );
                setAllCities(
                    data.location_list.map((city) => ({
                        label: city.location_name,
                        value: city.location_id,
                    }))
                );
            }
        } else {
            toast.error('Failed to load cities and pincodes. Please try again.');
        }
    };
    const handleAddressFormChange = (field, value) => {
        setAddressForm(prev => ({ ...prev, [field]: value }));
        if (addressValidationErrors[field]) {
            setAddressValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
        if (field === 'state_id') {
            setAddressForm(prev => ({ ...prev, district_id: '', district_name: '', location_id: '', location_name: '', pincode_id: '' }));
            setDistricts([]);
            setAllCities([]);
            setAllPincodes([]);
            if (value) {
                const selectedState = states.find(state => state.value === value);
                setAddressForm(prev => ({ ...prev, 
                    state_name: selectedState?.label || ''
                }));
                getDistricts(value);
            }
        } else if (field === 'district_id') {
            setAddressForm(prev => ({ ...prev, location_id: '', location_name: '', pincode_id: '' }));
            setAllCities([]);
            setAllPincodes([]);
            setFirstSelection(null);
            if (value) {
                const selectedDistrict = districts.find(district => district.value === value);
                setAddressForm(prev => ({ ...prev, district_name: selectedDistrict?.label || '' }));
                getlocationPincode(value);
            }
        } else if (field === 'location_id') {
            if (value) {
                if (!firstSelection) setFirstSelection('location');
                const selectedLocation = allCities.find(city => city.value === value);
                setAddressForm(prev => ({ ...prev, 
                    location_name: selectedLocation?.label || '', 
                    ...(firstSelection === 'location' ? { pincode_id: '' } : {})
                }));
            }
        } else if (field === 'pincode_id') {
            if (value) {
                if (!firstSelection) setFirstSelection('pincode');
                setAddressForm(prev => ({ ...prev, 
                    pincode_id: value, // Use the actual value, not the label
                    ...(firstSelection === 'pincode' ? {
                        location_id: '',
                        location_name: ''
                    } : {})
                }));
            }
        }
    }; 
    const validateAddressForm = () => {
        const errors = {};
        if (!addressForm.firm_name?.trim()) { errors.firm_name = 'Firm is required'; }
        if (!addressForm.address_line_1?.trim()) { errors.address_line_1 = 'Address line 1 is required'; }
        if (!addressForm.state_id) { errors.state_id = 'State is required'; }
        if (!addressForm.district_id) { errors.district_id = 'District is required'; }
        if (!addressForm.location_id) { errors.location_id = 'City is required'; }
        if (!addressForm.pincode_id) { errors.pincode_id = 'Pincode is required'; }
        setAddressValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSave = () => {
        if (!validateAddressForm()) return;
        if (gst_flow) {
            dispatch({
                type: 'UPDATE_OPERATIONAL_DETAILS',
                payload: {
                    ...addressForm,
                    pincode_id: addressForm.pincode_id
                }
            });
        } else {
            dispatch({
                type: 'UPDATE_OPERATIONAL_DETAILS', 
                payload: {
                    ...addressForm,
                    pincode_id: addressForm.pincode_id
                }
            });
        }
        onClose();
    };
    const handleClose = () => {
        setAddressValidationErrors({});
        onClose();
    };

    useEffect(() => {
        if (open && initialValidationErrors && Object.keys(initialValidationErrors).length > 0) {
            setAddressValidationErrors(initialValidationErrors);
        }
    }, [open, initialValidationErrors]);
    const getStates = async () => {
        setIsLoadingStates(true);
        const { data, error, message } = await GetAllStates();
        setIsLoadingStates(false);
        if (!error) {
            const stateOptions = data.map((state) => ({
                label: state.state_name,
                value: state.id,
            }));
            setStates(stateOptions);
            if (addressForm.state_id) {
                const matchingState = stateOptions.find(state => 
                    state.value === addressForm.state_id
                );
                if (matchingState) {
                    setAddressForm(prev => ({
                        ...prev,
                        state_id: matchingState.value,
                        state_name: matchingState.label
                    }));
                }
            }
        } else {
            return toast.error(message);
        }
    };
    const getDistricts = async (stateId) => {
        setDistricts([]);
        setIsLoadingDistricts(true);
        const { data, error, message } = await GetAllCityOfState(stateId);
        setIsLoadingDistricts(false);
        if (!error) {
            const districtOptions = data.map((district) => ({
                label: district.district_name,
                value: district.id,
            }));
            setDistricts(districtOptions);
        } 
        else return toast.error(message);
    };
    useEffect(() => {
        getStates();
    }, []);
    useEffect(() => {
        if (states.length > 0 && addressForm.state_id) {
            const matchingState = states.find(state => 
                state.value === addressForm.state_id
            );
            if (matchingState && addressForm.state_name !== matchingState.label) {
                setAddressForm(prev => ({
                    ...prev,
                    state_id: matchingState.value,
                    state_name: matchingState.label
                }));
            }
            getDistricts(addressForm.state_id);
        }
    }, [states, addressForm.state_id]);
    useEffect(() => {
      if(operational_details?.location_id) {
        const selectedLocation = allCities.find(city => city.value === operational_details?.location_id);
        if (selectedLocation) {
                setAddressForm(prev => ({ 
                    ...prev,
                    location_id: selectedLocation.value,
                    location_name: selectedLocation.label
                }));
            }
        }
    }, [operational_details?.location_id])
    useEffect(() => {
        if (addressForm.district_id) {
            getlocationPincode(addressForm.district_id);
        }
    }, [addressForm.district_id]);

    useEffect(() => {
        if (addressForm.pincode_id) {
            getlocationPincode(addressForm.district_id, null, addressForm.pincode_id);
        }
    }, [addressForm.pincode_id]);

    useEffect(() => {
        if (addressForm.location_id) {
            getlocationPincode(addressForm.district_id, addressForm.location_id, null);
        }
    }, [addressForm?.location_id]);

    useEffect(() => {
        if (allCities.length > 0 && allPincodes.length > 0) {
            const gstLocationId = operational_details?.location_id;
            const gstPincode = operational_details?.pincode_id;
            if (gstPincode) {
                const matchingPincode = allPincodes.find(pincode => pincode.value === gstPincode);
                if (matchingPincode && !addressForm?.pincode_id) {
                    setFirstSelection('pincode');
                    setAddressForm(prev => ({
                        ...prev,
                        pincode_id: matchingPincode.value
                    }));
                }
            }
            // Handle location prefill only if pincode is not prefilled
            else if (gstLocationId) {
                const matchingCity = allCities.find(city => city.value === gstLocationId);
                if (matchingCity && !addressForm?.location_id) {
                    setFirstSelection('location');
                    setAddressForm(prev => ({
                        ...prev,
                        location_id: matchingCity.value,
                        location_name: matchingCity.label
                    }));
                }
            }
        }
    }, [allCities, allPincodes]);
    return (
        <GlassModal
            open={open}
            onClose={handleClose}
            title="Edit Shop Details"
            maxWidth="600px"
            centerAlign
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <TextField
                            label={gst_flow ? 'GST' : 'PAN'}
                            fullWidth
                            value={gst_flow ? gst_details?.gst_number : operational_details.pan_number}
                            fetchedState={true}
                        />
                    </Box>
                    {gst_flow && (
                        <TextField
                            label="Firm Name"
                            fullWidth
                            value={operational_details && operational_details?.firm_name}
                            fetchedState={true}
                        />
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
                    {!gst_flow && (
                        <TextField
                            label="Firm Name*"
                            fullWidth
                            value={addressForm.firm_name}
                            onChange={(e) => handleAddressFormChange('firm_name', e.target.value)}
                            error={!!addressValidationErrors.firm_name}
                            helperText={addressValidationErrors.firm_name}
                            placeholder={"Enter Firm Name"}
                        />
                    )}
                    <TextField
                        label="Address line 1*"
                        fullWidth
                        value={addressForm.address_line_1}
                        onChange={(e) => handleAddressFormChange('address_line_1', e.target.value)}
                        error={!!addressValidationErrors.address_line_1}
                        helperText={addressValidationErrors.address_line_1}
                        placeholder={"Enter Address line 1"}
                    />
                    <TextField
                        label="Address line 2"
                        fullWidth
                        value={addressForm.address_line_2}
                        onChange={(e) => handleAddressFormChange('address_line_2', e.target.value)}
                        error={!!addressValidationErrors.address_line_2}
                        helperText={addressValidationErrors.address_line_2}
                        placeholder="Enter Address line 2"
                    />
                    
                    {gst_flow && (
                        <TextField
                            label="State"
                            fullWidth
                            value={addressForm.state_name}
                            fetchedState={true}
                        />
                    )}
                    {!gst_flow && (
                        <CustomSelect
                            label="State*"
                            fullWidth
                            value={addressForm.state_id}
                            onChange={(e) => {
                                handleAddressFormChange(
                                    'state_id', e.target.value
                                );
                            }}
                            error={!!addressValidationErrors.state_id}
                            helperText={addressValidationErrors.state_id}
                            displayEmpty
                            placeholder={"Select State"}
                            disabled={isLoadingStates}
                        >
                           {states.map((state) => (
                                <MenuItem key={state.value} value={state.value}>
                                    {state.label}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    )}
                    <CustomSelect
                        label="District*"
                        fullWidth
                        value={addressForm.district_id}
                        onChange={(e) => handleAddressFormChange('district_id', e.target.value)}
                        error={!!addressValidationErrors.district_id}
                        helperText={addressValidationErrors.district_id}
                        displayEmpty
                        placeholder={"Select District"}
                        disabled={!addressForm.state_id || isLoadingDistricts}
                    >
                        {districts.map((district) => (
                            <MenuItem key={district.value} value={district.value}>
                                {district.label}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <CustomSelect
                            label="City*"
                            fullWidth
                            placeholder={"Select City"}
                            value={addressForm.location_id}
                            onChange={(e) => handleAddressFormChange('location_id', e.target.value)}
                            error={!!addressValidationErrors.location_id}
                            helperText={addressValidationErrors.location_id}
                            displayEmpty
                            disabled={!addressForm.district_id || isLoadingCities}
                        >
                            {allCities.map((city) => (
                                <MenuItem key={city.value} value={city.value}>
                                    {city.label}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            label="Pincode*"
                            fullWidth
                            placeholder={"Select Pincode"}
                            value={addressForm.pincode_id}
                            onChange={(e) => handleAddressFormChange('pincode_id', e.target.value)}
                            error={!!addressValidationErrors.pincode_id}
                            helperText={addressValidationErrors.pincode_id}
                            displayEmpty
                            // disabled={allPincodes.length === 0}
                        >
                            {allPincodes.map((pincode) => (
                                <MenuItem key={pincode.value} value={pincode.value}>
                                    {pincode.label}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                </Box>
                <Box sx={{ pb: 2, display: 'flex', width: '100%',}}>
                    <CustomButton
                        fullWidth
                        variant="filled"
                        label="Continue"
                        onClick={handleSave}
                        loading={loading}
                        // disabled={!!addressValidationErrors && Object.keys(addressValidationErrors).length > 0}
                    />
                </Box>
            </Box>
        </GlassModal>
    );
};

export default AddressDetailModal;